import React from 'react';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
    size?: 'default' | 'lg';
}

const sizeClasses = {
    default: 'p-5',
    lg: 'p-6',
};

const CardContext = React.createContext<{ size: 'default' | 'lg' }>({ size: 'default' });

const CardRoot = React.forwardRef<HTMLDivElement, CardProps>(({ children, className = '', size = 'default', ...props }, ref) => {
    return (
        <CardContext.Provider value={{ size }}>
            <div
                ref={ref}
                className={`card-premium ${sizeClasses[size]} ${className}`}
                {...props}
            >
                {children}
            </div>
        </CardContext.Provider>
    );
});
CardRoot.displayName = 'Card';

const CardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, className = '', ...props }) => {
    const { size } = React.useContext(CardContext);
    const paddingBottom = size === 'lg' ? 'pb-8' : 'pb-6';
    return (
        <div className={`flex justify-between items-start mb-4 ${paddingBottom} -mx-6 -mt-6 px-6 pt-6 -mx-8 -mt-8 px-8 pt-8 ${className}`} {...props}>
            {children}
        </div>
    );
};
CardHeader.displayName = 'CardHeader';

const CardTitle: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({ children, className = '', ...props }) => (
    <h3 className={`text-card-title ${className}`} {...props}>
        {children}
    </h3>
);
CardTitle.displayName = 'CardTitle';

const CardDescription: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({ children, className = '', ...props }) => (
    <p className={`text-helper mt-1 ${className}`} {...props}>
        {children}
    </p>
);
CardDescription.displayName = 'CardDescription';

const CardContent: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, className = '', ...props }) => {
    const { size } = React.useContext(CardContext);
    const margin = size === 'lg' ? '-m-8' : '-m-6';
    return (
        <div className={` ${margin} mt-0 p-6 pt-0 ${className}`} {...props}>
            {children}
        </div>
    )
};
CardContent.displayName = 'CardContent';


export const Card = Object.assign(CardRoot, {
    Header: CardHeader,
    Title: CardTitle,
    Description: CardDescription,
    Content: CardContent,
});
